import React, { useState, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiMessageSquare, FiPhone, FiRefreshCw, FiSearch } = FiIcons;

// In-memory storage for webhook logs (in a real app, this would be a database)
let webhookLogs = [];

// Function to add logs (would be called by the webhook endpoint)
export const addWebhookLog = (log) => {
  webhookLogs.unshift({ // Add to beginning for newest first
    id: Date.now() + Math.random(),
    timestamp: new Date().toISOString(),
    ...log
  });
  // Keep only last 100 logs to prevent memory issues
  if (webhookLogs.length > 100) {
    webhookLogs = webhookLogs.slice(0, 100);
  }
};

const TwilioLogsView = () => {
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  useEffect(() => {
    // Load logs from memory
    setLogs([...webhookLogs]);
    setFilteredLogs([...webhookLogs]);
  }, []);

  useEffect(() => {
    // Filter logs based on search term and type
    let filtered = logs;

    if (searchTerm) {
      filtered = filtered.filter(log => 
        log.from?.includes(searchTerm) ||
        log.to?.includes(searchTerm) ||
        log.body?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (typeFilter) {
      filtered = filtered.filter(log => log.type === typeFilter);
    }

    setFilteredLogs(filtered);
  }, [logs, searchTerm, typeFilter]);

  const refreshLogs = () => {
    setLogs([...webhookLogs]);
  };

  const clearLogs = () => {
    webhookLogs = [];
    setLogs([]);
    setFilteredLogs([]);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'incoming-sms':
        return FiMessageSquare;
      case 'incoming-call':
        return FiPhone;
      default:
        return FiMessageSquare;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'incoming-sms':
        return 'bg-blue-100 text-blue-800';
      case 'incoming-call':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Webhooks & Logs</h2>
        <div className="flex space-x-2">
          <button
            onClick={refreshLogs}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <SafeIcon icon={FiRefreshCw} className="mr-2" />
            Refresh
          </button>
          <button
            onClick={clearLogs}
            className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Clear All
          </button>
        </div>
      </div>

      {/* Webhook Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-lg font-medium text-blue-900 mb-2">Webhook Configuration</h3>
        <p className="text-blue-800 mb-2">
          To receive incoming SMS and call events, configure your Twilio webhooks to point to:
        </p>
        <div className="bg-white p-3 rounded border font-mono text-sm">
          <div className="mb-1">
            <strong>SMS Webhook URL:</strong> {window.location.origin}/api/twilio/webhooks/incoming-sms
          </div>
          <div>
            <strong>Voice Webhook URL:</strong> {window.location.origin}/api/twilio/webhooks/incoming-call
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by phone number or message content..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Types</option>
            <option value="incoming-sms">Incoming SMS</option>
            <option value="incoming-call">Incoming Call</option>
          </select>
        </div>
      </div>

      {/* Logs Table */}
      <div className="bg-white rounded-lg shadow-sm border">
        {filteredLogs.length === 0 ? (
          <div className="text-center py-12">
            <SafeIcon icon={FiMessageSquare} className="mx-auto text-4xl text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No webhook events yet</h3>
            <p className="text-gray-600">
              {logs.length === 0 
                ? "Incoming SMS and call events will appear here once your webhooks are configured."
                : "No events match your current filters."
              }
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    From
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    To
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Content
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timestamp
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredLogs.map((log) => (
                  <tr key={log.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <SafeIcon icon={getTypeIcon(log.type)} className="mr-2 text-gray-400" />
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(log.type)}`}>
                          {log.type}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {log.from}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {log.to}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="max-w-xs">
                        {log.type === 'incoming-sms' ? (
                          <div className="truncate" title={log.body}>
                            {log.body}
                          </div>
                        ) : (
                          <span className="text-gray-500 italic">Call event</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        log.status === 'received' ? 'bg-green-100 text-green-800' :
                        log.status === 'processed' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {log.status || 'received'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(log.timestamp).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default TwilioLogsView;